import {
    Box, Typography, Button, MenuItem, Select, FormControl, InputLabel,
    TextField, CircularProgress, Chip, OutlinedInput, Checkbox, ListItemText,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Toast } from 'primereact/toast';
import CustomAxios from "../../utils/CustomAxios";

/**
 * Granting wallet bonus to a chosen set of customers.
 *
 * The screen is arranged around the one number that matters before anybody
 * clicks: TOTAL COST. Amount times audience is the figure that turns a typo into
 * a bill, so it is the largest thing on the page and it is recomputed by the
 * server rather than in the browser — the same call that resolves the audience,
 * so the cost and the recipient count can never come from different queries.
 *
 * Nothing here credits on load. Preview is a POST that only reads; Execute is a
 * separate, explicit action.
 */

const PINK = '#ED1E79';
const INK = '#16121A';
const MUTED = '#6E6478';
const LINE = '#E9E3EE';
const CHIP = '#F4EFF7';
const OK = '#0B7A5E';
const WARN = '#B4472F';

/** Must match SEGMENTS in the backend's user_reports.py. */
const SEGMENTS = [
    'Active Recurring User',
    'Repeat User',
    'Single Order User',
    'Churned User',
    'Never Ordered',
];

const AUDIENCES = [
    { key: 'default', label: 'Everyone in the hubs' },
    { key: 'subscribers', label: 'Active subscribers' },
    { key: 'expired_subscribers', label: 'Lapsed subscribers' },
    { key: 'non_subscribers', label: 'Non-subscribers' },
];

/**
 * The sentinel for "an exact moment, not a rolling window".
 *
 * -1 rather than a string, because every other value in EXPIRIES is a number and
 * a mixed-type value list is how a `===` comparison quietly stops matching.
 * Never sent to the server: `expiry_days` goes null when this is chosen and
 * `expires_at` carries the instruction instead.
 */
const CUSTOM = -1;

/** Expiry presets. 0 is "never", and it is a deliberate choice, not a blank. */
const EXPIRIES = [
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days (default)' },
    { value: 180, label: '180 days' },
    { value: 0, label: 'Never expires' },
    { value: CUSTOM, label: 'Custom date & time…' },
];

const money = (v) => `₹ ${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export function BonusGrants() {
    const toast = useRef(null);
    const inventory = useSelector((state) => state.inventory.inventory) || [];

    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');
    const [expiryDays, setExpiryDays] = useState(90);
    /*
     * The exact expiry, as the two halves an admin actually types.
     *
     * Split into date and time rather than one datetime-local input because
     * datetime-local gives no seconds unless you ask for `step`, and browsers
     * disagree about what it then renders. Two plain inputs — a date, and a
     * time with step=1 — produce hh:mm:ss everywhere, and combine into an ISO
     * string with no timezone suffix, which the server reads in its own zone.
     */
    const [expiryDate, setExpiryDate] = useState('');
    const [expiryTime, setExpiryTime] = useState('23:59:59');
    const isCustomExpiry = expiryDays === CUSTOM;

    /** ISO local, or null. Never a partial string — a date with no time is not a moment. */
    const expiresAt = useMemo(() => {
        if (!isCustomExpiry || !expiryDate) return null;
        const time = /^\d{2}:\d{2}(:\d{2})?$/.test(expiryTime) ? expiryTime : '23:59:59';
        return `${expiryDate}T${time.length === 5 ? `${time}:00` : time}`;
    }, [isCustomExpiry, expiryDate, expiryTime]);
    const [hubIds, setHubIds] = useState([]);
    const [segments, setSegments] = useState([]);
    const [audience, setAudience] = useState('default');

    /*
     * The people, and the ones taken out by hand.
     *
     * `excluded` is kept as a Set of ids rather than by inverting a selection:
     * the audience changes as filters change, and a "selected" list would have to
     * be recomputed against every new pool -- silently re-including somebody the
     * admin had removed. Exclusions survive a filter change, which is the
     * behaviour an admin expects and the thing the grant stores.
     */
    const [people, setPeople] = useState(null);
    const [allIds, setAllIds] = useState([]);
    const [excluded, setExcluded] = useState(() => new Set());
    const [userSearch, setUserSearch] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [showPeople, setShowPeople] = useState(false);

    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState('');
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [grants, setGrants] = useState([]);

    const body = useMemo(() => ({
        amount: amount === '' ? null : Number(amount),
        // Exactly one of the two carries the instruction. Sending both would
        // leave the server to guess, and expiry_for would silently prefer the
        // date — a rule better stated here than discovered there.
        expiry_days: isCustomExpiry ? null : expiryDays,
        expires_at: expiresAt,
        inventory_ids: hubIds,
        segments,
        audience,
        excluded_ids: [...excluded],
    }), [amount, expiryDays, isCustomExpiry, expiresAt, hubIds, segments, audience, excluded]);

    /** The audience the people table is listing, without the amount. */
    const audienceBody = useMemo(() => ({
        inventory_ids: hubIds, segments, audience,
    }), [hubIds, segments, audience]);

    const loadPeople = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const res = await CustomAxios.post('bonus-grants/users/', {
                ...audienceBody, search: userSearch, limit: 100,
            });
            setPeople(res?.data?.users || []);
            setAllIds(res?.data?.all_ids || []);
        } catch (e) {
            setPeople([]);
            setAllIds([]);
        } finally {
            setLoadingUsers(false);
        }
    }, [audienceBody, userSearch]);

    // Only once the table is open. Listing 1,433 customers to render a collapsed
    // panel would be a scan nobody asked for.
    useEffect(() => {
        if (!showPeople) return undefined;
        const t = setTimeout(loadPeople, userSearch ? 320 : 0);
        return () => clearTimeout(t);
    }, [showPeople, loadPeople, userSearch]);

    const runPreview = useCallback(async () => {
        // No amount means nothing to price. Asking the server would answer with a
        // validation error the admin has not earned yet.
        if (amount === '' || Number(amount) <= 0) {
            setPreview(null); setPreviewError('');
            return;
        }
        /*
         * A half-typed custom expiry is not a question worth asking either.
         * Without this the request goes out with expiry_days null AND
         * expires_at null, which the server reads as "use the 90-day default" —
         * so the preview would quietly price a grant with an expiry the admin
         * did not choose and is not looking at.
         */
        if (isCustomExpiry && !expiresAt) {
            setPreview(null);
            setPreviewError('Pick the expiry date and time.');
            return;
        }
        setLoading(true);
        try {
            const res = await CustomAxios.post('bonus-grants/preview/', body);
            setPreview(res?.data || null);
            setPreviewError('');
        } catch (e) {
            setPreview(null);
            setPreviewError(e?.response?.data?.error || 'Could not work out the audience.');
        } finally {
            setLoading(false);
        }
    }, [body, amount, isCustomExpiry, expiresAt]);

    /*
     * Debounced, because this scans orders to work out segments. Re-running it on
     * every keystroke of the amount field would be a scan per character for a
     * number that only multiplies the answer.
     */
    useEffect(() => {
        const t = setTimeout(runPreview, 420);
        return () => clearTimeout(t);
    }, [runPreview]);

    const loadGrants = useCallback(async () => {
        try {
            const res = await CustomAxios.get('bonus-grants/');
            setGrants(res?.data?.grants || []);
        } catch (e) {
            setGrants([]);
        }
    }, []);
    useEffect(() => { loadGrants(); }, [loadGrants]);

    const execute = async () => {
        if (!name.trim()) {
            toast.current?.show({
                severity: 'warn', summary: 'Name it',
                detail: 'A grant needs a name — it is how you find it afterwards.', life: 4000,
            });
            return;
        }
        setRunning(true);
        try {
            const res = await CustomAxios.post('bonus-grants/', {
                ...body, name: name.trim(), execute: true,
            });
            const d = res?.data || {};
            const parts = [`${d.credited || 0} credited`];
            // Said out loud, both of them. "Already credited" is not an error, and a
            // remaining count is the difference between finished and stopped.
            if (d.already_credited) parts.push(`${d.already_credited} already had it`);
            if (d.remaining) parts.push(`${d.remaining} left — run it again`);
            if (d.failed) parts.push(`${d.failed} failed`);
            toast.current?.show({
                severity: d.failed ? 'warn' : 'success',
                summary: `${money(d.total_credited)} granted`,
                detail: parts.join(' · '), life: 7000,
            });
            loadGrants();
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'Grant failed',
                detail: e?.response?.data?.error || 'Nothing was credited.', life: 6000,
            });
        } finally {
            setRunning(false);
        }
    };

    const continueGrant = async (id) => {
        setRunning(true);
        try {
            const res = await CustomAxios.post(`bonus-grants/${id}/execute/`, {});
            const d = res?.data || {};
            toast.current?.show({
                severity: 'success', summary: `${d.credited || 0} more credited`,
                detail: d.remaining ? `${d.remaining} still to go.` : 'Finished.',
                life: 6000,
            });
            loadGrants();
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'Could not continue',
                detail: e?.response?.data?.error || '', life: 5000,
            });
        } finally {
            setRunning(false);
        }
    };

    const label = { fontSize: 13 };
    const field = { backgroundColor: "#fff", fontSize: 13 };

    return (
        <Box sx={{ padding: { xs: "12px", md: "22px" }, display: "flex", flexDirection: "column", gap: "18px" }}>
            <Toast ref={toast} />

            <Box>
                <Typography sx={{ fontSize: "21px", fontWeight: 750, letterSpacing: "-0.02em", color: INK }}>
                    Wallet bonus grants
                </Typography>
                <Typography sx={{ fontSize: "13px", color: MUTED, marginTop: "2px" }}>
                    Promotional credit, held apart from the money customers topped up themselves.
                    It expires, and it is spent before their own balance is.
                </Typography>
            </Box>

            {/* ---- who and how much ---- */}
            <Box sx={{
                display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: "12px", backgroundColor: CHIP, border: `1px solid ${LINE}`,
                borderRadius: "12px", padding: "14px",
            }}>
                <TextField size="small" label="Grant name" value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Diwali 2026 — lapsed customers"
                    InputLabelProps={{ sx: label }} sx={{ backgroundColor: "#fff" }} />

                <TextField size="small" label="Amount per customer (₹)" value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                    inputMode="decimal" InputLabelProps={{ sx: label }}
                    sx={{ backgroundColor: "#fff" }} />

                <FormControl size="small">
                    <InputLabel id="exp-l" sx={label}>Expires after</InputLabel>
                    <Select labelId="exp-l" label="Expires after" value={expiryDays}
                        onChange={(e) => setExpiryDays(e.target.value)} sx={{ ...field, height: 38 }}>
                        {EXPIRIES.map((x) => (
                            <MenuItem key={x.value} value={x.value} sx={label}>{x.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/*
                  An exact moment, to the second.

                  The presets above can only say "90 days from whenever this
                  runs", which cannot express the case this is most often wanted
                  for: credit that must die when a promotion closes, at 23:59:59
                  on the last day — not at whatever time of day the admin
                  happened to press the button.

                  Two inputs rather than one datetime-local: that control hides
                  seconds unless given a `step`, and browsers disagree about what
                  it renders even then. A date plus a time with step=1 gives
                  hh:mm:ss everywhere.
                */}
                {isCustomExpiry && (
                    <>
                        <TextField
                            size="small" type="date" label="Expiry date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: label }}
                            sx={{ backgroundColor: '#fff' }}
                        />
                        <TextField
                            size="small" type="time" label="Expiry time (hh:mm:ss)"
                            value={expiryTime}
                            onChange={(e) => setExpiryTime(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: label }}
                            // step=1 is what puts the seconds box on screen.
                            inputProps={{ step: 1 }}
                            sx={{ backgroundColor: '#fff' }}
                            helperText={
                                expiryDate
                                    ? `Credit lapses ${expiryDate} at ${expiryTime || '23:59:59'}`
                                    : 'Pick a date to set the exact expiry'
                            }
                        />
                    </>
                )}

                <FormControl size="small">
                    <InputLabel id="aud-l" sx={label}>Subscription state</InputLabel>
                    <Select labelId="aud-l" label="Subscription state" value={audience}
                        onChange={(e) => setAudience(e.target.value)} sx={{ ...field, height: 38 }}>
                        {AUDIENCES.map((a) => (
                            <MenuItem key={a.key} value={a.key} sx={label}>{a.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel id="hub-l" sx={label}>Hubs</InputLabel>
                    <Select labelId="hub-l" multiple label="Hubs" value={hubIds}
                        onChange={(e) => setHubIds(e.target.value)}
                        input={<OutlinedInput label="Hubs" />}
                        renderValue={(picked) => picked.length ? `${picked.length} hub(s)` : 'All hubs'}
                        sx={{ ...field, height: 38 }}>
                        {inventory.map((h) => {
                            const id = h.value ?? h.id;
                            return (
                                <MenuItem key={id} value={id} sx={label}>
                                    <Checkbox checked={hubIds.includes(id)} size="small" sx={{ padding: "2px 6px 2px 0" }} />
                                    <ListItemText primary={h.name || h.place || `Hub ${id}`}
                                        primaryTypographyProps={{ sx: label }} />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <FormControl size="small">
                    <InputLabel id="seg-l" sx={label}>Order history</InputLabel>
                    <Select labelId="seg-l" multiple label="Order history" value={segments}
                        onChange={(e) => setSegments(e.target.value)}
                        input={<OutlinedInput label="Order history" />}
                        renderValue={(picked) => picked.length ? `${picked.length} segment(s)` : 'Any'}
                        sx={{ ...field, height: 38 }}>
                        {SEGMENTS.map((s) => (
                            <MenuItem key={s} value={s} sx={label}>
                                <Checkbox checked={segments.includes(s)} size="small" sx={{ padding: "2px 6px 2px 0" }} />
                                <ListItemText primary={s} primaryTypographyProps={{ sx: label }} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* ---- who, exactly ---- */}
            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", backgroundColor: "#fff", overflow: "hidden" }}>
                <Box onClick={() => setShowPeople((v) => !v)}
                    sx={{
                        padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px",
                        cursor: "pointer", borderBottom: showPeople ? `1px solid ${LINE}` : "none",
                    }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                        Choose people
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {preview?.ok
                            ? `${Number(preview.total_users).toLocaleString('en-IN')} in this audience`
                            : 'pick a hub or segment first'}
                    </Typography>
                    {excluded.size > 0 && (
                        <Chip size="small" label={`${excluded.size} removed`}
                            sx={{ backgroundColor: "#FDF0EC", color: WARN, fontSize: 11, fontWeight: 700 }} />
                    )}
                    <Box sx={{ marginLeft: "auto", color: PINK, fontWeight: 700, fontSize: 13 }}>
                        {showPeople ? 'Hide' : 'Show'}
                    </Box>
                </Box>

                {showPeople && (
                    <>
                        <Box sx={{ padding: "10px 16px", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", backgroundColor: CHIP }}>
                            <TextField size="small" placeholder="Search name or mobile"
                                value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                                sx={{ backgroundColor: "#fff", minWidth: 220 }} />
                            {/*
                              Clearing exclusions is one action, not one per row. An
                              admin who has removed forty people and changed their mind
                              should not have to tick forty boxes.
                            */}
                            <Button size="small" disabled={!excluded.size}
                                onClick={() => setExcluded(new Set())}
                                sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 650, color: PINK }}>
                                Restore all {excluded.size ? `(${excluded.size})` : ''}
                            </Button>
                            <Button size="small" disabled={!allIds.length}
                                onClick={() => setExcluded(new Set(allIds))}
                                sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 650, color: MUTED }}>
                                Remove all
                            </Button>
                            <Typography sx={{ fontSize: 11.5, color: MUTED, marginLeft: "auto" }}>
                                {people ? `showing ${people.length} of ${Number(preview?.total_users || allIds.length).toLocaleString('en-IN')}, most orders first` : ''}
                            </Typography>
                        </Box>

                        {loadingUsers ? (
                            <Box sx={{ display: "flex", justifyContent: "center", padding: "28px" }}>
                                <CircularProgress size={22} sx={{ color: PINK }} />
                            </Box>
                        ) : !people?.length ? (
                            <Box sx={{ padding: "26px", textAlign: "center", color: MUTED, fontSize: 13 }}>
                                No customers in this audience.
                            </Box>
                        ) : (
                            <Box sx={{ maxHeight: "46vh", overflowY: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                        <tr>
                                            {['', 'Customer', 'Mobile', 'Orders', 'Last order', 'Segment'].map((h, i) => (
                                                <th key={h + i} style={{
                                                    position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1,
                                                    textAlign: i === 3 ? "right" : "left", padding: "9px 14px",
                                                    borderBottom: `1px solid ${LINE}`, fontSize: "10.5px",
                                                    letterSpacing: ".08em", textTransform: "uppercase", color: MUTED,
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {people.map((u) => {
                                            const off = excluded.has(u.user_id);
                                            return (
                                                <tr key={u.user_id}
                                                    onClick={() => setExcluded((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(u.user_id)) next.delete(u.user_id);
                                                        else next.add(u.user_id);
                                                        return next;
                                                    })}
                                                    style={{ cursor: "pointer", opacity: off ? 0.45 : 1 }}>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}` }}>
                                                        <Checkbox checked={!off} size="small" sx={{ padding: 0, color: MUTED, "&.Mui-checked": { color: PINK } }} />
                                                    </td>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}`, color: INK }}>{u.name}</td>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}`, color: MUTED, fontVariantNumeric: "tabular-nums" }}>{u.mobileno}</td>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}`, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 650 }}>{u.orders}</td>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}`, color: MUTED }}>{u.last_order_at || '—'}</td>
                                                    <td style={{ padding: "8px 14px", borderBottom: `1px solid ${LINE}` }}>
                                                        <Chip size="small" label={u.segment}
                                                            sx={{ fontSize: 10.5, fontWeight: 700, backgroundColor: CHIP, color: MUTED }} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ---- what it would cost ---- */}
            {previewError ? (
                <Box sx={{
                    border: `1px solid ${WARN}`, backgroundColor: "#FDF0EC", borderRadius: "12px",
                    padding: "12px 15px", color: WARN, fontSize: 13, fontWeight: 600,
                }}>{previewError}</Box>
            ) : null}

            {preview?.ok ? (
                <Box sx={{
                    border: `1px solid ${LINE}`, borderRadius: "14px", backgroundColor: "#fff",
                    padding: "16px 18px", display: "flex", flexDirection: "column", gap: "13px",
                }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "26px", alignItems: "flex-end" }}>
                        <Box>
                            <Typography sx={{ fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                Total cost
                            </Typography>
                            {/* The figure that turns a typo into a bill. */}
                            <Typography sx={{ fontSize: "34px", fontWeight: 800, letterSpacing: "-0.02em", color: PINK, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
                                {money(preview.total_cost)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                Customers
                            </Typography>
                            <Typography sx={{ fontSize: "24px", fontWeight: 750, color: INK, fontVariantNumeric: "tabular-nums" }}>
                                {Number(preview.total_users).toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                Each
                            </Typography>
                            <Typography sx={{ fontSize: "24px", fontWeight: 750, color: INK, fontVariantNumeric: "tabular-nums" }}>
                                {money(preview.amount)}
                            </Typography>
                        </Box>
                        <Chip size="small" label={preview.audience}
                            sx={{ backgroundColor: CHIP, color: INK, fontSize: 12, fontWeight: 650 }} />
                        <Chip size="small"
                            label={preview.never_expires
                                ? 'Never expires'
                                : `Expires ${new Date(preview.expires_at).toLocaleDateString('en-IN')}`}
                            sx={{
                                backgroundColor: preview.never_expires ? "#FDF0EC" : "#EAF6F0",
                                color: preview.never_expires ? WARN : OK, fontSize: 12, fontWeight: 700,
                            }} />
                    </Box>

                    {preview.needs_multiple_runs ? (
                        <Typography sx={{ fontSize: 12.5, color: WARN, fontWeight: 600 }}>
                            More than {Number(preview.batch_limit).toLocaleString('en-IN')} customers, so this
                            runs in batches. Press Grant again afterwards to continue — nobody is paid twice.
                        </Typography>
                    ) : null}

                    {preview.sample?.length ? (
                        <Box>
                            <Typography sx={{ fontSize: 11.5, color: MUTED, marginBottom: "5px" }}>
                                First {preview.returned} of {Number(preview.total_users).toLocaleString('en-IN')}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                {preview.sample.map((u) => (
                                    <Chip key={u.user_id} size="small"
                                        label={`${u.name} · ${u.hub}`}
                                        sx={{ backgroundColor: CHIP, fontSize: 11, color: MUTED }} />
                                ))}
                            </Box>
                        </Box>
                    ) : null}

                    <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <Button variant="contained" disabled={running || loading || !preview.total_users}
                            onClick={execute}
                            sx={{
                                textTransform: "none", fontWeight: 700, fontSize: 13.5, borderRadius: "10px",
                                padding: "9px 20px", backgroundColor: PINK, "&:hover": { backgroundColor: "#D01A6C" },
                            }}>
                            {running ? 'Granting…' : `Grant ${money(preview.total_cost)}`}
                        </Button>
                        {loading ? <CircularProgress size={18} sx={{ color: PINK }} /> : null}
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                            Safe to press twice — each customer is credited once per grant.
                        </Typography>
                    </Box>
                </Box>
            ) : loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", padding: "30px" }}>
                    <CircularProgress size={24} sx={{ color: PINK }} />
                </Box>
            ) : (
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                    Enter an amount to see who it reaches and what it costs.
                </Typography>
            )}

            {/* ---- what has been granted before ---- */}
            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden", backgroundColor: "#fff" }}>
                <Box sx={{ padding: "11px 16px", borderBottom: `1px solid ${LINE}` }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>Past grants</Typography>
                </Box>
                {!grants.length ? (
                    <Box sx={{ padding: "30px", textAlign: "center", color: MUTED, fontSize: 13 }}>
                        Nothing granted yet.
                    </Box>
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead><tr>
                                {['Grant', 'Each', 'Credited', 'Already had it', 'Failed', 'Total', 'Status', ''].map((h) => (
                                    <th key={h} style={{
                                        textAlign: "left", padding: "9px 14px", borderBottom: `1px solid ${LINE}`,
                                        fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase",
                                        color: MUTED, whiteSpace: "nowrap",
                                    }}>{h}</th>
                                ))}
                            </tr></thead>
                            <tbody>
                                {grants.map((g) => (
                                    <tr key={g.id}>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}` }}>
                                            <Box sx={{ fontWeight: 650, color: INK }}>{g.name}</Box>
                                            <Box sx={{ fontSize: 11, color: MUTED }}>{g.audience}</Box>
                                        </td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}`, fontVariantNumeric: "tabular-nums" }}>{money(g.amount)}</td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}`, fontVariantNumeric: "tabular-nums" }}>{g.credited_users}</td>
                                        {/* Not a failure. Someone paid by an earlier run of the same grant. */}
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}`, color: MUTED, fontVariantNumeric: "tabular-nums" }}>{g.already_credited || '—'}</td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}`, color: g.failed_users ? WARN : MUTED, fontVariantNumeric: "tabular-nums" }}>{g.failed_users || '—'}</td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}`, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{money(g.total_credited)}</td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}` }}>
                                            <Chip size="small" label={g.status}
                                                sx={{
                                                    fontSize: 10.5, fontWeight: 700,
                                                    backgroundColor: g.status === 'DONE' ? "#EAF6F0" : g.status === 'FAILED' ? "#FDF0EC" : CHIP,
                                                    color: g.status === 'DONE' ? OK : g.status === 'FAILED' ? WARN : MUTED,
                                                }} />
                                            {g.note ? (
                                                <Box sx={{ fontSize: 11, color: MUTED, marginTop: "3px" }}>{g.note}</Box>
                                            ) : null}
                                        </td>
                                        <td style={{ padding: "9px 14px", borderBottom: `1px solid ${LINE}` }}>
                                            {g.status === 'RUNNING' ? (
                                                <Button size="small" variant="outlined" disabled={running}
                                                    onClick={() => continueGrant(g.id)}
                                                    sx={{ textTransform: "none", fontSize: 12, fontWeight: 650, borderColor: PINK, color: PINK }}>
                                                    Continue
                                                </Button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default BonusGrants;
